import React, { useState } from "react";
import { 
  FiPlus, 
  FiCheckCircle, 
  FiClock, 
  FiArrowRight, 
  FiExternalLink, 
  FiShield, 
  FiAlertCircle,
  FiUser
} from "react-icons/fi";
import type { RcaActionItem, RcaCapaHandoff } from "../../../types/rca.types.js";

export type RcaActionsCapaHandoffProps = {
  actions?: RcaActionItem[] | undefined;
  capaHandoff?: RcaCapaHandoff | undefined;
  onAddAction?: ((action: Partial<RcaActionItem>) => void) | undefined;
  onNavigateToCapa?: ((capaRef: string) => void) | undefined;
  readOnly?: boolean | undefined;
};

const INITIAL_ACTIONS: RcaActionItem[] = [
  {
    id: "act-1",
    actionCode: "ACT-2026-0001",
    description: "Recalibrate CNC machine Z-axis tool offset and spindle zero-point",
    type: "Corrective",
    ownerName: "John Smith",
    targetDate: "30-Aug-2026",
    status: "Open",
    effectiveness: "Pending",
  },
  {
    id: "act-2",
    actionCode: "ACT-2026-0002",
    description: "Update calibration operating procedure SOP-CNC-04",
    type: "Corrective",
    ownerName: "Kumar",
    targetDate: "02-Sep-2026",
    status: "In Progress",
    effectiveness: "Pending",
  },
  {
    id: "act-3",
    actionCode: "ACT-2026-0003",
    description: "Train operators on periodic gauge re-zeroing check",
    type: "Preventive",
    ownerName: "Arun",
    targetDate: "05-Sep-2026",
    status: "Open",
    effectiveness: "—",
  },
];

export function RcaActionsCapaHandoff({
  actions = INITIAL_ACTIONS,
  capaHandoff = {
    capaRequired: true,
    capaReference: "CAPA-2026-0008",
    handedOverOn: "25-Aug-2026",
    handoffBy: "John Smith",
    notes: "Root cause verified. Corrective and preventive actions defined and handed over to CAPA owner.",
    status: "HANDED_OVER"
  },
  onAddAction,
  onNavigateToCapa,
  readOnly = false,
}: RcaActionsCapaHandoffProps) {
  const [activeTab, setActiveTab] = useState<"CORRECTIVE" | "PREVENTIVE">("CORRECTIVE");
  const [actionList, setActionList] = useState<RcaActionItem[]>(actions && actions.length > 0 ? actions : INITIAL_ACTIONS);
  const [capaRequired, setCapaRequired] = useState<boolean>(capaHandoff.capaRequired);
  const [notes, setNotes] = useState<string>(capaHandoff.notes);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [newDesc, setNewDesc] = useState<string>("");
  const [newType, setNewType] = useState<"Corrective" | "Preventive">("Corrective");
  const [newOwner, setNewOwner] = useState<string>("John Smith");
  const [newTargetDate, setNewTargetDate] = useState<string>("2026-09-10");

  React.useEffect(() => {
    if (actions && actions.length > 0) {
      setActionList(actions);
    }
  }, [actions]);

  const handleCreateAction = () => {
    if (!newDesc.trim() || readOnly) return;
    const newAction: RcaActionItem = {
      id: `act-${Date.now()}`,
      actionCode: `ACT-2026-${String(actionList.length + 1).padStart(4, "0")}`,
      description: newDesc.trim(),
      type: newType,
      ownerName: newOwner.trim() || "John Smith",
      targetDate: newTargetDate,
      status: "Open",
      effectiveness: "Pending",
    };
    setActionList([...actionList, newAction]);
    onAddAction?.(newAction);
    setShowAddModal(false);
    setNewDesc("");
  };

  const filtered = actionList.filter(a => activeTab === "CORRECTIVE" ? a.type === "Corrective" : a.type === "Preventive");

  return (
    <div className="space-y-5">
      {/* Top Header */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">CMTS &gt; Actions & CAPA Governance</div>
          <h2 className="text-lg font-bold text-slate-900 mt-0.5">6. Actions & CAPA Handoff</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Define corrective and preventive actions, with Complaint-anchored provenance handoff to CAPA.
          </p>
        </div>

        {!readOnly && (
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-1.5 transition-all"
          >
            <FiPlus className="w-4 h-4" />
            Add Action
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center gap-2">
        <button
          type="button"
          onClick={() => setActiveTab("CORRECTIVE")}
          className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
            activeTab === "CORRECTIVE"
              ? "bg-blue-600 text-white shadow-sm"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          Corrective Actions ({actions.filter(a => a.type === "Corrective").length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("PREVENTIVE")}
          className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
            activeTab === "PREVENTIVE"
              ? "bg-blue-600 text-white shadow-sm"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          Preventive Actions ({actions.filter(a => a.type === "Preventive").length})
        </button>
      </div>

      {/* Actions Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200 uppercase text-[11px]">
              <tr>
                <th className="py-3 px-4">Action ID</th>
                <th className="py-3 px-3">Action Description</th>
                <th className="py-3 px-3">Type</th>
                <th className="py-3 px-3">Owner</th>
                <th className="py-3 px-3">Target Date</th>
                <th className="py-3 px-3">Status</th>
                <th className="py-3 px-3">Effectiveness</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(action => (
                <tr key={action.id} className="hover:bg-slate-50 transition-colors">
                  <td className="py-3 px-4 font-bold text-blue-600">{action.actionCode}</td>
                  <td className="py-3 px-3 font-semibold text-slate-900">{action.description}</td>
                  <td className="py-3 px-3">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold border ${
                      action.type === "Corrective" 
                        ? "bg-blue-50 text-blue-700 border-blue-200" 
                        : "bg-purple-50 text-purple-700 border-purple-200"
                    }`}>
                      {action.type}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-slate-700 font-medium">{action.ownerName}</td>
                  <td className="py-3 px-3 text-slate-500 font-mono text-[11px]">{action.targetDate}</td>
                  <td className="py-3 px-3">
                    <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                      {action.status}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-slate-500">{action.effectiveness}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Handoff to CAPA Card */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <FiShield className="w-4 h-4 text-blue-600" />
            Handoff to CAPA (Complaint Provenance)
          </h3>

          {/* CAPA Required Toggle */}
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-slate-700">CAPA Required:</span>
            <input
              type="checkbox"
              checked={capaRequired}
              disabled={readOnly}
              onChange={(e) => setCapaRequired(e.target.checked)}
              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
            />
          </div>
        </div>

        {capaRequired && (
          <div className="space-y-3 text-xs">
            <div className="p-3.5 bg-blue-50/70 rounded-xl border border-blue-200 flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="text-slate-500">Linked CAPA Reference: </span>
                <strong className="text-blue-700 font-bold">{capaHandoff.capaReference}</strong>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Handed over by {capaHandoff.handoffBy} on {capaHandoff.handedOverOn}
                </div>
              </div>

              {onNavigateToCapa && (
                <button
                  type="button"
                  onClick={() => onNavigateToCapa(capaHandoff.capaReference || "CAPA-2026-0008")}
                  className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-xs flex items-center gap-1 shadow-sm transition-colors"
                >
                  Go to CAPA Record <FiExternalLink className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div>
              <label className="block text-slate-700 font-semibold mb-1">CAPA Handoff Notes & Containment Evidence:</label>
              <textarea
                rows={3}
                disabled={readOnly}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 font-medium focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Add Action Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 max-w-md w-full p-6 space-y-4">
            <h3 className="text-base font-bold text-slate-900">Add Investigation Action</h3>
            <p className="text-xs text-slate-500">
              Define a corrective or preventive action to be tracked or handed off to CAPA.
            </p>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Action Description:</label>
                <textarea
                  rows={2}
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="e.g. Recalibrate CNC spindle zero point and update tool offset"
                  className="w-full p-2 rounded-lg border border-slate-300 bg-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Action Type:</label>
                  <select
                    value={newType}
                    onChange={(e) => setNewType(e.target.value as "Corrective" | "Preventive")}
                    className="w-full p-2 rounded-lg border border-slate-300 bg-white"
                  >
                    <option value="Corrective">Corrective</option>
                    <option value="Preventive">Preventive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Owner Name:</label>
                  <input
                    type="text"
                    value={newOwner}
                    onChange={(e) => setNewOwner(e.target.value)}
                    className="w-full p-2 rounded-lg border border-slate-300 bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Target Date:</label>
                <input
                  type="date"
                  value={newTargetDate}
                  onChange={(e) => setNewTargetDate(e.target.value)}
                  className="w-full p-2 rounded-lg border border-slate-300 bg-white"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-xs font-semibold hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!newDesc.trim()}
                onClick={handleCreateAction}
                className={`px-4 py-2 rounded-lg text-xs font-semibold shadow-sm transition-all ${
                  newDesc.trim() 
                    ? "bg-blue-600 hover:bg-blue-700 text-white" 
                    : "bg-slate-200 text-slate-400 cursor-not-allowed"
                }`}
              >
                Save Action
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
